<template lang="pug">

uc-lottie.uc-asset.max-w-full(
  :class="twMerge('w-[16rem]', classAttr)"
  :src="fullSrc"
  :style="[styleAttr, { width: width, opacity: opacity }]"
  v-bind="$attrs"
  v-if="isSrcLottie"
)

uc-icon.uc-asset.max-w-full.text-primary-500(
  :as-image="true"
  :class="twMerge('w-[16rem]', classAttr)"
  :icon="src"
  :style="[styleAttr, { width: width, opacity: opacity }]"
  v-bind="$attrs"
  v-else-if="isSrcIcon"
)

img.uc-asset.max-w-full(
  :alt="alt || ''"
  :class="[twMerge('w-[16rem]', classAttr), { 'uc-asset-editable': isEditModeActive && isAssetEditable }]"
  :ref="elFnRef"
  :src="fullSrc"
  :style="[styleAttr, { width: width, opacity: opacity }]"
  :title="alt || undefined"
  v-bind="$attrs"
  v-else-if="isSrcImg"
  :editPath="editPath || null"
  @click="isEditModeActive && isAssetEditable ? openEditLightbox('lightbox-upload-image', editPath) : null"
)

video.uc-asset.max-w-full.auto-alpha-0(
  :alt="alt || ''"
  :autoplay="false"
  :class="twMerge('w-[16rem]', classAttr)"
  :controls="false"
  :ref="elFnRef"
  :src="fullSrc"
  :style="[styleAttr, { width: width, opacity: opacity }]"
  :loop="!dontLoopVideo"
  defaultMuted="true"
  muted="true"
  onerror="this.style.display = 'none'"
  onplay="setTimeout(()=>this.classList.add('auto-alpha-1'),1000);"
  playsInline="true"
  uc-autoplay=""
  v-bind="$attrs"
  v-else-if="isSrcMp4"
)

span.uc-asset.max-w-full.rounded-full.inline-flex.items-center.justify-center(
  :class="twMerge('leading-[0.5] w-12 h-12 text-white bg-primary-700', classAttr)"
  :style="[styleAttr, { width: width, height: width, opacity: opacity }]"
  uc-dont-read=""
  uc-word-skip=""
  v-bind="$attrs"
  v-else-if="isSrcNumber"
) 
  span.subheading.uc-asset-number.max-w-full(
    :style="{ fontSize: size ? size * 0.5 + 'rem' : null }"
  ) {{ src }}
  span.sr-only .

uc-themed-svg.uc-asset.max-w-full(
  :alt="alt || ''"
  :class="[twMerge('w-[16rem]', classAttr), , { 'uc-asset-editable': isEditModeActive && isAssetEditable }]"
  :src="fullSrc"
  :style="[styleAttr, { width: width, opacity: opacity }]"
  :title="alt || undefined"
  v-bind="$attrs"
  v-else-if="isSrcSvg"
  :editPath="editPath || null"
  @click="isEditModeActive && isAssetEditable ? openEditLightbox('lightbox-upload-image', editPath) : null"
)

</template>

<script lang="ts">
export default {
  inheritAttrs: false
};
</script>
<script setup lang="ts">
import { computed, onMounted, useAttrs, ref, inject, watch } from 'vue';
import { twMerge } from '../lib/mergeStyles';
import isNumber from '../lib/isNumber';
import expandUploadPath from '../lib/expandUploadPath';
import openLightbox from '../lib/openLightbox';
import useEditMode from '../stores/useEditMode';
import useSuspend from '../lib/useSuspend';
const { setEditScreen } = useEditMode();
const suspend = useSuspend();

const props = defineProps<{
  src: string | number;
  alt?: string;
  size?: number;
  opacity?: string;
  editPath?: string;
}>();

const isEditModeActive = inject('isEditModeActive') as Ref<boolean>;
const sectionId = inject('sectionId', null) as string | null;

const fullSrc = computed(() => expandUploadPath(props.src));

// const key = ref(1);

const elRef = ref();
const elFnRef = (el: HTMLElement) => (elRef.value = el);

const classAttr = computed(() => useAttrs().class as string | undefined);
const styleAttr = computed(() => useAttrs().style as string | undefined);

const isString = function isString(v: unknown): v is string {
  return typeof v === 'string';
};

const isSrcMp4 = computed(
  () => isString(props.src) && props.src.includes('.mp4')
);

const isSrcSvg = computed(
  () =>
    isString(props.src) &&
    // ($|\?.*$) allows for query string after extension, like e.g. file.jpg?v=1
    /\.(p|s|primary|secondary)-\w+\.svg($|\?.*$)/i.test(props.src)
);

const isSrcImg = computed(
  () =>
    !isSrcSvg.value &&
    isString(props.src) &&
    // ($|\?.*$) allows for query string after extension, like e.g. file.jpg?v=1
    /\.(png|jpg|jpeg|gif|png|svg)($|\?.*$)/i.test(props.src)
);

const isSrcIcon = computed(
  () => isString(props.src) && /\w+:\w+/.test(props.src)
);

const isSrcNumber = computed(() => isNumber(props.src));

const isSrcLottie = computed(
  () =>
    // ($|\?.*$) allows for query string after extension, like e.g. file.jpg?v=1
    isString(props.src) && /\.json($|\?.*$)/i.test(props.src)
);

const dontLoopVideo = computed(
  () =>
    // check if 'loop=false' is present in the query string
    isSrcMp4.value && /[?&]loop=false/.test(props.src)
);

const width = computed(() => (props.size ? props.size + 'rem' : ''));

const isAssetEditable = computed(() => {
  // check if editing is allowed. Only allowed for img & svg for now
  return props.editPath ? isSrcSvg.value || isSrcImg.value : false;
});

// set the current editmode window props and open the lightbox
const openEditLightbox = async (lightbox: string, editPath: string) => {
  if (!lightbox || !editPath) return;
  if (sectionId) await setEditScreen(sectionId, editPath);

  openLightbox(lightbox);
};

onMounted(() => {
  if (
    !isSrcSvg.value &&
    !isSrcImg.value &&
    !isSrcIcon.value &&
    !isSrcNumber.value &&
    !isSrcMp4.value &&
    !isSrcLottie.value
  ) {
    console.warn('Image src not recognized: ', props.src);
  }

  if (isSrcMp4.value && elRef.value && suspend.reducedMotion) {
    const video = elRef.value as HTMLVideoElement;
    video.pause();
    video.currentTime = 0;
    video.classList.add('auto-alpha-1');
  }
});

watch(
  () => suspend.reducedMotion,
  reduced => {
    if (!isSrcMp4.value || !elRef.value) return;
    const video = elRef.value as HTMLVideoElement;
    if (reduced) {
      video.pause();
      video.classList.add('auto-alpha-1');
    } else {
      video.play();
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.on('publicwatch:update', async ({ path }) => {
    const name = path.split(/[\\/]/).slice(-1)[0];
    const src = props.src;

    if (isNumber(src)) return;
    if (!String(src).includes(name)) return;

    await fetch(src, { cache: 'reload', mode: 'no-cors' });

    if (elRef.value) {
      elRef.value.setAttribute('src', src);
    }
  });
}
</script>

<style scoped>
/* *:where(img, svg) {
  object-fit: contain;
  object-position: center;
  width: 100%;
} */

.uc-asset .uc-asset-number {
  @apply inline bg-transparent text-xl tracking-normal text-white;
}

.uc-asset-editable {
  @apply cursor-pointer;
  opacity: 0.6;
  border-style: dashed;
  border-width: 4px;
  border-radius: 0.75rem;
  border-color: rgb(67, 56, 202);
}
</style>
